import { useState, useEffect } from "react";
import { NavLink, useLocation as useRouterLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Settings,
  Building2,
  BarChart3,
  CreditCard,
  UserCog,
  Code,
  Bell,
  CalendarClock,
  Armchair,
  Link2,
  PhoneCall,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ListChecks,
  Crown,
  UsersRound,
  Store,
  X,
  Settings2,
  HelpCircle,
  LucideIcon,
  MessageSquareQuote,
  Mail,
  MessageCircle,
  FileEdit,
  Megaphone,
  Phone as PhoneIcon,
  CalendarCheck,
  UserCircle2,
  ClipboardList,
  DoorOpen,
  SlidersHorizontal,
  Smartphone,
  History,
  Activity,
  Globe,
  UtensilsCrossed,
  GraduationCap,
  MessagesSquare,
  LifeBuoy,
  Sparkles,
  Rocket,
  Eye,
  FlaskConical,
  Brain,
  Share2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useLocation } from "@/contexts/LocationContext";
import { useImpersonation } from "@/hooks/useImpersonation";
import { usePermissions } from "@/hooks/usePermissions";
import type { PermissionKey } from "@/lib/permissions";
import apiClient from "@/lib/api-client";
import LocationSwitcher from "./LocationSwitcher";

// Tipos para los elementos del menú
interface MenuItem {
  name: string;
  to: string;
  icon: LucideIcon;
  /** Permiso requerido para ver el ítem (solo aplica a `staff`; owner/admin siempre pasan). */
  permission?: PermissionKey;
}

interface MenuGroup {
  name: string;
  icon: LucideIcon;
  children: MenuItem[];
}

type NavigationItem = MenuItem | MenuGroup;

// Función para verificar si es un grupo
const isMenuGroup = (item: NavigationItem): item is MenuGroup => {
  return "children" in item;
};

// Navegación principal con grupos
const navigation: NavigationItem[] = [
  { name: "Panel", to: "/", icon: LayoutDashboard },
  { name: "Reservas", to: "/reservations", icon: Calendar },
  // Módulo Clases (add-on por sede): categoría principal que SUSTITUYE a
  // "Reservas" cuando la sede actual lo tiene activo (filtrado más abajo).
  { name: "Clases", to: "/classes", icon: GraduationCap },
  { name: "Citas", to: "/appointments", icon: CalendarCheck },
  { name: "Clientes", to: "/customers", icon: Users },
  {
    name: "Mi Local",
    icon: Settings2,
    children: [
      {
        name: "Horarios",
        to: "/schedule",
        icon: CalendarClock,
        permission: "horario:editar",
      },
      {
        name: "Mapa de Mesas",
        to: "/tables",
        icon: Armchair,
        permission: "mesas:editar",
      },
      { name: "Combinaciones", to: "/table-combinations", icon: Link2 },
      {
        name: "Menús",
        to: "/menus",
        icon: UtensilsCrossed,
        permission: "mesas:editar",
      },
      { name: "Servicios", to: "/services", icon: ClipboardList },
      { name: "Profesionales", to: "/specialists", icon: UserCircle2 },
      { name: "Salas", to: "/appointment-rooms", icon: DoorOpen },
      {
        name: "Configuración",
        to: "/location-settings",
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    name: "Carmen IA",
    icon: Sparkles,
    children: [
      {
        name: "Centralita IA",
        to: "/voice-ai",
        icon: PhoneCall,
        permission: "integraciones:gestionar",
      },
      {
        name: "Conversaciones",
        to: "/conversations",
        icon: MessagesSquare,
        permission: "conversaciones:gestionar",
      },
    ],
  },
  {
    name: "Marketing",
    icon: Rocket,
    children: [
      {
        name: "Campañas",
        to: "/campaigns",
        icon: Megaphone,
        permission: "campanas:gestionar",
      },
      {
        name: "Mi página web",
        to: "/public-profile",
        icon: Globe,
        permission: "perfil_publico:editar",
      },
      { name: "Mi Testimonio", to: "/testimonial", icon: MessageSquareQuote },
    ],
  },
  {
    name: "Mi negocio",
    icon: Store,
    children: [
      {
        name: "Equipo",
        to: "/team",
        icon: UsersRound,
        permission: "equipo:gestionar",
      },
      {
        name: "Ficha del negocio",
        to: "/locations",
        icon: Building2,
        permission: "sedes:gestionar",
      },
      {
        name: "Integración",
        to: "/integration",
        icon: Code,
        permission: "integraciones:gestionar",
      },
      {
        name: "Suscripción",
        to: "/subscription",
        icon: Crown,
        permission: "facturacion:gestionar",
      },
    ],
  },
  { name: "Notificaciones", to: "/notifications", icon: Bell },
  { name: "Centro de Ayuda", to: "/help", icon: HelpCircle },
  { name: "Configuración", to: "/settings", icon: Settings },
];

const adminNavigation: NavigationItem[] = [
  { name: "Estadísticas", to: "/admin/analytics", icon: BarChart3 },
  {
    name: "Clientes",
    icon: Building2,
    children: [
      { name: "Organizaciones", to: "/admin/organizations", icon: Building2 },
      { name: "Salud de clientes", to: "/admin/health", icon: Activity },
      { name: "Usuarios", to: "/admin/users", icon: UserCog },
      { name: "Clientes App", to: "/admin/endusers", icon: Smartphone },
      {
        name: "Visibilidad en la app",
        to: "/admin/app-visibility",
        icon: Eye,
      },
      {
        name: "Módulo Clases",
        to: "/admin/classes-module",
        icon: GraduationCap,
      },
      {
        name: "Accesos como cliente",
        to: "/admin/impersonation-logs",
        icon: History,
      },
    ],
  },
  {
    name: "Soporte",
    icon: LifeBuoy,
    children: [
      { name: "Conversaciones", to: "/admin/soporte", icon: MessagesSquare },
      // Instagram y página de Facebook de ReservaManager: lo que escriban por
      // ahí entra en Conversaciones (5-oct-2026).
      { name: "Redes sociales", to: "/admin/redes-sociales", icon: Share2 },
      { name: "Tickets", to: "/admin/tickets", icon: HelpCircle },
    ],
  },
  {
    name: "IA",
    icon: Sparkles,
    children: [
      { name: "WhatsApp IA", to: "/admin/whatsapp-ai", icon: MessagesSquare },
      // Conectar el número de WhatsApp que el negocio ya usa, sin que deje
      // de funcionar en su móvil (coexistencia de Meta, 7-oct-2026).
      { name: "WhatsApp de siempre", to: "/admin/whatsapp-coexistencia", icon: Smartphone },
      // "Cerebro IA": análisis de llamadas + añadido de reglas que rige a
      // Carmen en voz Y WhatsApp (la ruta conserva su nombre histórico).
      { name: "Cerebro IA", to: "/admin/voice-ai", icon: PhoneIcon },
      // "Probar a Carmen" (sep-2026): simulador de chat EN SECO contra el
      // cerebro de un negocio concreto, sin gastar WhatsApp ni llamar.
      { name: "Probar a Carmen", to: "/admin/carmen-sim", icon: FlaskConical },
      // "Habla con Carmen" (sep-2026): chat libre con el cerebro de Carmen
      // (como con Claude) que propone cambios de prompt para aplicar.
      { name: "Habla con Carmen", to: "/admin/carmen-brain", icon: Brain },
    ],
  },
  {
    name: "Marketing",
    icon: Megaphone,
    children: [
      { name: "Google Ads", to: "/admin/google-ads", icon: Megaphone },
      { name: "Meta Ads", to: "/admin/meta-ads", icon: Megaphone },
      { name: "Analítica web", to: "/admin/web-analytics", icon: BarChart3 },
    ],
  },
  {
    name: "Comunicaciones",
    icon: Mail,
    children: [
      { name: "Emails", to: "/admin/emails", icon: Mail },
      {
        name: "Plantillas Email",
        to: "/admin/email-templates",
        icon: FileEdit,
      },
      { name: "Campañas", to: "/admin/campaigns", icon: Megaphone },
      { name: "WhatsApp", to: "/admin/whatsapp", icon: MessageCircle },
      { name: "Push App", to: "/admin/push-broadcast", icon: Bell },
    ],
  },
  {
    name: "Contenido",
    icon: Globe,
    children: [
      { name: "Páginas web", to: "/admin/public-profiles", icon: Globe },
      {
        name: "Testimonios",
        to: "/admin/testimonials",
        icon: MessageSquareQuote,
      },
    ],
  },
  {
    name: "Plataforma",
    icon: CreditCard,
    children: [
      { name: "Planes", to: "/admin/plans", icon: CreditCard },
      {
        name: "Estados Reserva",
        to: "/admin/reservation-statuses",
        icon: ListChecks,
      },
    ],
  },
];

// Componente para un item simple del menú
function MenuItemComponent({
  item,
  isCollapsed,
  isMobile,
  onClose,
  badge,
}: {
  item: MenuItem;
  isCollapsed: boolean;
  isMobile: boolean;
  onClose?: () => void;
  badge?: number;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClose}
      title={!isMobile && isCollapsed ? item.name : undefined}
      className={({ isActive }) =>
        `flex items-center ${!isMobile && isCollapsed ? "justify-center" : "gap-3"} px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? "bg-primary-50 text-primary-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
        }`
      }
    >
      <span className="relative flex-shrink-0">
        <item.icon size={20} className="flex-shrink-0" />
        {!!badge && !isMobile && isCollapsed && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      {(isMobile || !isCollapsed) && (
        <span className="flex-1 flex items-center justify-between gap-2 min-w-0">
          <span className="flex items-center gap-1.5 truncate">
            {item.name}
          </span>
          {!!badge && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </span>
      )}
    </NavLink>
  );
}

/** Chip rojo de pendientes (mismo estilo que el de los ítems sueltos). */
function BadgeChip({ count }: { count: number }) {
  return (
    <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
      {count > 99 ? "99+" : count}
    </span>
  );
}

// Componente para un grupo con submenú
function MenuGroupComponent({
  group,
  isCollapsed,
  isMobile,
  onClose,
  badges,
}: {
  group: MenuGroup;
  isCollapsed: boolean;
  isMobile: boolean;
  onClose?: () => void;
  /** Pendientes por ruta de hijo; la cabecera muestra la suma. */
  badges?: Record<string, number>;
}) {
  const routerLocation = useRouterLocation();

  // Verificar si algún hijo está activo para abrir el menú por defecto
  // (también rutas anidadas, p. ej. /admin/organizations/:id).
  const isChildActive = group.children.some(
    (child) =>
      routerLocation.pathname === child.to ||
      routerLocation.pathname.startsWith(`${child.to}/`),
  );

  // Estado abierto/cerrado recordado por grupo (conveniencia por navegador;
  // si localStorage no está disponible, simplemente no se recuerda).
  const storageKey = `rm-sidebar-open:${group.name}`;
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (isChildActive) return true;
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  const toggleOpen = () =>
    setIsOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        // p. ej. modo privado: sin memoria, sin drama
      }
      return next;
    });

  // Al navegar a un hijo con el grupo cerrado, abrirlo (sin persistir: es una
  // apertura de cortesía, no una elección del usuario).
  useEffect(() => {
    if (isChildActive) setIsOpen(true);
  }, [isChildActive]);

  const groupBadge = group.children.reduce(
    (sum, child) => sum + (badges?.[child.to] ?? 0),
    0,
  );

  // En modo colapsado, mostrar solo el icono con tooltip
  if (!isMobile && isCollapsed) {
    return (
      <div className="relative group">
        <button
          className={`flex items-center justify-center w-full px-3 py-2 rounded-lg transition-colors ${
            isChildActive
              ? "bg-primary-50 text-primary-700"
              : "text-gray-700 hover:bg-gray-100"
          }`}
          title={group.name}
        >
          <span className="relative flex-shrink-0">
            <group.icon size={20} className="flex-shrink-0" />
            {groupBadge > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-semibold flex items-center justify-center">
                {groupBadge > 9 ? "9+" : groupBadge}
              </span>
            )}
          </span>
        </button>

        {/* Tooltip con submenú en hover cuando está colapsado */}
        <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
          <div className="bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[180px]">
            <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
              {group.name}
            </p>
            {group.children.map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-50 text-primary-700 font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`
                }
              >
                <child.icon size={16} className="flex-shrink-0" />
                <span className="flex-1">{child.name}</span>
                {(badges?.[child.to] ?? 0) > 0 && (
                  <BadgeChip count={badges?.[child.to] ?? 0} />
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Modo expandido o móvil: menú desplegable normal
  return (
    <div>
      <button
        onClick={toggleOpen}
        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors ${
          isChildActive
            ? "bg-primary-50 text-primary-700"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <group.icon size={20} className="flex-shrink-0" />
          <span className="truncate">{group.name}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Con el grupo abierto los pendientes ya se ven en cada hijo */}
          {groupBadge > 0 && !isOpen && <BadgeChip count={groupBadge} />}
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Submenú desplegable (sin tope de altura: con 5+ hijos, max-h-48 recortaba) */}
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
          {group.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              <child.icon size={16} className="flex-shrink-0" />
              <span className="flex-1 truncate">{child.name}</span>
              {(badges?.[child.to] ?? 0) > 0 && (
                <BadgeChip count={badges?.[child.to] ?? 0} />
              )}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { user } = useAuth();
  const {
    isCollapsed,
    isMobile,
    isMobileOpen,
    toggleSidebar,
    closeMobileSidebar,
  } = useSidebar();
  const { locations, currentBookingMode, currentLocation } = useLocation();
  const { isImpersonating } = useImpersonation();
  const { can } = usePermissions();
  const isSuperAdmin = user?.role === "owner";
  const hasMultipleLocations = locations.length > 1;
  const showLocationSwitcher = isSuperAdmin || hasMultipleLocations;

  // Distintivo de "Páginas web" pendientes de revisar, solo para superadmin.
  const [pendingProfilesCount, setPendingProfilesCount] = useState(0);
  useEffect(() => {
    if (!isSuperAdmin) return;
    apiClient
      .get("/admin/public-profiles/counts")
      .then((res) => setPendingProfilesCount(res.data.data?.pending_review || 0))
      .catch(() => {});
  }, [isSuperAdmin]);

  // Badge de "Conversaciones" (WhatsApp IA, FASE 3b): conversaciones en modo
  // "human" con mensajes sin leer, esperando a una persona. Solo se sondea si
  // el ítem va a ser visible (el permiso ya filtra `filteredNavigation` más
  // abajo) y solo mientras la pestaña está visible — mismo patrón anti-spam
  // que `NotificationsDropdown`/`ChangeRequestsBell` (evita ráfagas de
  // peticiones en pestañas de fondo, ver notas del 22-jul-2026).
  const canSeeConversations = can("conversaciones:gestionar");
  const [conversationsCount, setConversationsCount] = useState(0);
  useEffect(() => {
    if (!canSeeConversations) {
      setConversationsCount(0);
      return;
    }

    let interval: ReturnType<typeof setInterval> | null = null;

    const fetchCount = () => {
      apiClient
        .get("/whatsapp-conversations/count")
        .then((res) => setConversationsCount(res.data.data?.humanCount || 0))
        .catch(() => {});
    };

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const startPolling = () => {
      if (interval !== null) return;
      interval = setInterval(fetchCount, 60000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchCount();
        startPolling();
      } else {
        stopPolling();
      }
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [canSeeConversations]);

  // Badge de "Soporte" (inbox de plataforma, superadmin): mismo patrón
  // anti-spam que el de "Conversaciones" de arriba (solo sondea si el ítem
  // va a ser visible — aquí, solo superadmin — y solo con la pestaña
  // visible).
  const [platformSupportCount, setPlatformSupportCount] = useState(0);
  useEffect(() => {
    if (!isSuperAdmin) {
      setPlatformSupportCount(0);
      return;
    }

    let interval: ReturnType<typeof setInterval> | null = null;

    const fetchCount = () => {
      apiClient
        .get("/admin/platform-conversations/count")
        .then((res) => setPlatformSupportCount(res.data.data?.humanCount || 0))
        .catch(() => {});
    };

    const stopPolling = () => {
      if (interval !== null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const startPolling = () => {
      if (interval !== null) return;
      interval = setInterval(fetchCount, 60000);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchCount();
        startPolling();
      } else {
        stopPolling();
      }
    };

    handleVisibilityChange();
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isSuperAdmin]);

  // Sprint 1 + 2 — filtrar nav según bookingMode EFECTIVO (org impersonada si es
  // superadmin, propia org en caso contrario — ver LocationContext.currentBookingMode).
  // - En modo 'appointments' ocultamos rutas de mesas/salas/combinaciones y la vista de reservas.
  // - En modo 'tables' ocultamos rutas de citas/servicios/profesionales.
  const bookingMode = currentBookingMode;
  // Sede con el módulo de clases activo (es una escuela, no un salón).
  const classesActive =
    bookingMode === "tables" && currentLocation?.classesEnabled === true;
  const filteredNavigation = navigation
    .map((item) => {
      if (!isMenuGroup(item)) {
        if (item.permission && !can(item.permission)) return null;
        if (bookingMode === "appointments" && item.to === "/reservations")
          return null;
        if (bookingMode === "tables" && item.to === "/appointments")
          return null;
        // Sede con el módulo de clases activo: es una escuela, no un salón →
        // "Clases" sustituye a "Reservas" como categoría principal.
        if (item.to === "/classes" && !classesActive) return null;
        if (item.to === "/reservations" && classesActive) return null;
        return item;
      }
      if (bookingMode === "appointments") {
        const filteredChildren = item.children
          .filter(
            (child) =>
              child.to !== "/tables" &&
              child.to !== "/table-combinations" &&
              child.to !== "/menus" &&
              child.to !== "/location-settings",
          )
          .filter((child) => !child.permission || can(child.permission))
          // En modo appointments, "Horarios" apunta a la nueva config de citas
          .map((child) =>
            child.to === "/schedule"
              ? { ...child, to: "/appointments/settings" }
              : child,
          );
        if (filteredChildren.length === 0) return null;
        return { ...item, children: filteredChildren };
      }
      // bookingMode === 'tables' → ocultar servicios/profesionales/salas del grupo
      const filteredChildren = item.children
        .filter(
          (child) =>
            child.to !== "/services" &&
            child.to !== "/specialists" &&
            child.to !== "/appointment-rooms" &&
            // Sede de clases: fuera TODO lo de mesas (decisión 14-oct:
            // ocultar antes que adaptar). Los días cerrados y excepciones
            // de la escuela se gestionan en /classes → pestaña Calendario.
            // El grupo "Mi Local" queda vacío y desaparece entero.
            (!classesActive ||
              (child.to !== "/tables" &&
                child.to !== "/table-combinations" &&
                child.to !== "/menus" &&
                child.to !== "/schedule" &&
                child.to !== "/location-settings")),
        )
        .filter((child) => !child.permission || can(child.permission));
      if (filteredChildren.length === 0) return null;
      return { ...item, children: filteredChildren };
    })
    .filter((item): item is NavigationItem => item !== null);

  // Pendientes por ruta: los grupos suman los de sus hijos en la cabecera.
  const navBadges: Record<string, number> = {
    "/conversations": conversationsCount,
  };
  const adminBadges: Record<string, number> = {
    "/admin/soporte": platformSupportCount,
    "/admin/public-profiles": pendingProfilesCount,
  };

  // En móvil, no mostrar nada si no está abierto
  if (isMobile && !isMobileOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay para móvil */}
      {isMobile && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      <aside
        className={`bg-white border-r border-gray-200 fixed left-0 bottom-0 overflow-y-auto transition-all duration-300 z-50 ${
          isImpersonating
            ? "top-[calc(104px+var(--safe-top))]"
            : "top-[calc(64px+var(--safe-top))]"
        } ${isMobile ? "w-64" : isCollapsed ? "w-16" : "w-64"}`}
      >
        {/* Toggle Button - Solo en desktop */}
        {!isMobile && (
          <button
            onClick={toggleSidebar}
            className="absolute -right-3 top-6 bg-white border border-gray-200 rounded-full p-1 shadow-sm hover:bg-gray-50 transition-colors z-10"
            title={isCollapsed ? "Expandir menú" : "Colapsar menú"}
          >
            {isCollapsed ? (
              <ChevronRight size={16} />
            ) : (
              <ChevronLeft size={16} />
            )}
          </button>
        )}

        {/* Close Button - Solo en móvil */}
        {isMobile && (
          <button
            onClick={closeMobileSidebar}
            className="absolute right-2 top-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Cerrar menú"
          >
            <X size={20} />
          </button>
        )}

        <nav
          className={`p-4 space-y-1 ${!isMobile && isCollapsed ? "px-2" : ""} ${isMobile ? "pt-12" : ""}`}
        >
          {/* Location Switcher - Show for superadmin always, or for users with multiple locations */}
          {showLocationSwitcher && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <LocationSwitcher isCollapsed={!isMobile && isCollapsed} />
            </div>
          )}

          {filteredNavigation.map((item) =>
            isMenuGroup(item) ? (
              <MenuGroupComponent
                key={item.name}
                group={item}
                isCollapsed={!isMobile && isCollapsed}
                isMobile={isMobile}
                onClose={isMobile ? closeMobileSidebar : undefined}
                badges={navBadges}
              />
            ) : (
              <MenuItemComponent
                key={item.to}
                item={item}
                isCollapsed={!isMobile && isCollapsed}
                isMobile={isMobile}
                onClose={isMobile ? closeMobileSidebar : undefined}
                badge={navBadges[item.to]}
              />
            ),
          )}

          {/* Admin Navigation */}
          {isSuperAdmin && (
            <div
              className={`pt-6 mt-6 border-t border-gray-200 ${!isMobile && isCollapsed ? "border-t-0 pt-4 mt-4" : ""}`}
            >
              {(isMobile || !isCollapsed) && (
                <p className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Superadmin
                </p>
              )}
              {!isMobile && isCollapsed && (
                <div className="w-full h-px bg-gray-200 mb-3" />
              )}

              {adminNavigation.map((item) =>
                isMenuGroup(item) ? (
                  <MenuGroupComponent
                    key={item.name}
                    group={item}
                    isCollapsed={!isMobile && isCollapsed}
                    isMobile={isMobile}
                    onClose={isMobile ? closeMobileSidebar : undefined}
                    badges={adminBadges}
                  />
                ) : (
                  <MenuItemComponent
                    key={item.to}
                    item={item}
                    isCollapsed={!isMobile && isCollapsed}
                    isMobile={isMobile}
                    onClose={isMobile ? closeMobileSidebar : undefined}
                    badge={adminBadges[item.to]}
                  />
                ),
              )}
            </div>
          )}
        </nav>
      </aside>
    </>
  );
}
